import { useEffect, useMemo, useState } from "react";

import {
  buildDifferenceCellState,
  pairQuotedWithRequestedPlans,
} from "../utilities/buildDifferenceCellState";

import type { SbsCellStateMap } from "../components/PlanEditor/context/SbsCellStateContext";
import type {
  ComparableCarrierGroup,
  ComparableRow,
} from "../utilities/buildDifferenceCellState";
import type { GridApi } from "ag-grid-community";

// Events after which the grid's row values may have changed: rows replaced
// (a refetch or a carrier reorder) or a cell edited.
const ROW_VALUE_EVENTS = [
  "firstDataRendered",
  "modelUpdated",
  "rowDataUpdated",
  "cellValueChanged",
] as const;

const readRows = (api: GridApi): ComparableRow[] => {
  const rows: ComparableRow[] = [];
  api.forEachNode((node) => {
    if (node.data?.benefitKey) {
      rows.push(node.data);
    }
  });

  return rows;
};

/**
 * Drops the pairs whose quoted plan is a pad slot or an awaiting-quote column.
 * Its empty cells are not a carrier's answer, and a dismissal could not be
 * saved against it because no proposal plan backs its id.
 */
const withoutSynthesizedPlans = (
  pairs: ReadonlyMap<string, string>,
  synthesizedPlanMaps: readonly (ReadonlyMap<string, unknown> | undefined)[],
): ReadonlyMap<string, string> =>
  new Map(
    [...pairs].filter(
      ([quotedId]) => !synthesizedPlanMaps.some((map) => map?.has(quotedId)),
    ),
  );

interface UseQuotesDifferenceMapParams {
  api: GridApi | null;
  /** The carrier id the page gives its requested column. */
  requestedCarrierId: string;
  /**
   * The Quotes grid data. Undefined while it loads, when nothing is flagged.
   * The two maps hold the synthesized pad-slot and awaiting-quote plans, which
   * have no proposal plan behind them.
   */
  carrierGridData:
    | {
        carrierGroups: readonly ComparableCarrierGroup[];
        padFillByPlanId?: ReadonlyMap<string, unknown>;
        placeholderMaterializeByPlanId?: ReadonlyMap<string, unknown>;
      }
    | undefined;
  /** The quoted value a flag was dismissed at; see `buildDifferenceCellState`. */
  dismissedValueOf: (
    planId: string,
    benefitKey: string,
  ) => string | null | undefined;
  /** Whether custom text is a cell's active value; see `buildDifferenceCellState`. */
  hasCustomText?: (
    planId: string,
    benefitKey: string,
    network?: string,
  ) => boolean;
}

/**
 * The "different than requested" cell state for the Quotes grid, derived from
 * the rows the grid is showing. It reads them through the grid API because a
 * row merges several stored fields under one benefit key, so the page-level
 * plan data does not line up with the cells.
 */
export const useQuotesDifferenceMap = ({
  api,
  requestedCarrierId,
  carrierGridData,
  dismissedValueOf,
  hasCustomText,
}: UseQuotesDifferenceMapParams): SbsCellStateMap => {
  const [differenceMap, setDifferenceMap] = useState<SbsCellStateMap>({});
  const pairs = useMemo(
    () =>
      withoutSynthesizedPlans(
        pairQuotedWithRequestedPlans(
          carrierGridData?.carrierGroups ?? [],
          requestedCarrierId,
        ),
        [
          carrierGridData?.padFillByPlanId,
          carrierGridData?.placeholderMaterializeByPlanId,
        ],
      ),
    [
      requestedCarrierId,
      carrierGridData?.carrierGroups,
      carrierGridData?.padFillByPlanId,
      carrierGridData?.placeholderMaterializeByPlanId,
    ],
  );

  useEffect(() => {
    if (!api) {
      // Keep the existing empty map rather than minting a new one, so a caller
      // whose inputs change identity every render cannot loop on this effect.
      setDifferenceMap((prev) => (Object.keys(prev).length === 0 ? prev : {}));

      return undefined;
    }

    const recompute = (): void => {
      const next = buildDifferenceCellState({
        rows: readRows(api),
        pairs,
        dismissedValueOf,
        hasCustomText,
      });
      // Keep the previous object when nothing changed so the syncer's effect
      // does not fire on every grid event.
      setDifferenceMap((prev) =>
        JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
      );
    };

    recompute();
    ROW_VALUE_EVENTS.forEach((event) => api.addEventListener(event, recompute));

    return () => {
      ROW_VALUE_EVENTS.forEach((event) =>
        api.removeEventListener(event, recompute),
      );
    };
  }, [api, pairs, dismissedValueOf, hasCustomText]);

  return differenceMap;
};
